/**
 * useCompareLensEvaluation — Multi-role lens comparison (DRR-103).
 *
 * Compares lens evaluation results across multiple roles side-by-side.
 * Returns universally hidden/visible fields and mixed-access diffs.
 *
 * Requires scope:admin or staff:manage.
 */

import { useCallback, useState } from 'react';
import { gql } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL Operation
// ---------------------------------------------------------------------------

const COMPARE_LENS_EVALUATION = gql`
  query CompareLensEvaluation($input: CompareLensInput!) {
    compareLensEvaluation(input: $input) {
      roles {
        roleIds
        label
        scopes
        hiddenFields
        hiddenFieldCount
        triggeredRuleId
      }
      universallyHidden
      universallyVisible
      mixedAccessFields {
        fieldName
        visibleTo
        hiddenFrom
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LensComparisonRole {
  roleIds: string[];
  label: string;
  scopes: string[];
  hiddenFields: string[];
  hiddenFieldCount: number;
  triggeredRuleId: string | null;
}

export interface FieldAccessDiff {
  fieldName: string;
  visibleTo: string[];
  hiddenFrom: string[];
}

export interface LensComparisonResult {
  roles: LensComparisonRole[];
  universallyHidden: string[];
  universallyVisible: string[];
  mixedAccessFields: FieldAccessDiff[];
}

export interface RoleSetInput {
  roleIds: string[];
  label: string;
}

export interface CompareLensInput {
  roleSets: RoleSetInput[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseCompareLensEvaluationReturn {
  comparison: LensComparisonResult | null;
  isComparing: boolean;
  error: Error | null;
  compareLenses: (input: CompareLensInput) => Promise<void>;
  clearComparison: () => void;
}

export function useCompareLensEvaluation(): UseCompareLensEvaluationReturn {
  const [comparison, setComparison] =
    useState<LensComparisonResult | null>(null);

  const [compareQuery, { loading, error }] =
    useLazyQuery<{ compareLensEvaluation: LensComparisonResult }>(
      COMPARE_LENS_EVALUATION,
    );

  const compareLenses = useCallback(
    async (input: CompareLensInput) => {
      try {
        const { data } = await compareQuery({ variables: { input } });
        if (data?.compareLensEvaluation) {
          setComparison(data.compareLensEvaluation);
        }
      } catch (err) {
        console.error('Lens comparison failed:', err);
      }
    },
    [compareQuery],
  );

  const clearComparison = useCallback(() => {
    setComparison(null);
  }, []);

  return {
    comparison,
    isComparing: loading,
    error: error ?? null,
    compareLenses,
    clearComparison,
  };
}
